import { Controller } from "@hotwired/stimulus";
import { calculateHalfLife, formatDuration, interpretation } from "../half_life.js";

const FREQUENCY_LABELS = {
  "once-daily": "Daily",
  "twice-daily": "2x daily",
  weekly: "Weekly",
};

export default class extends Controller {
  static targets = [
    "halfLife",
    "frequency",
    "error",
    "results",
    "steadyState",
    "peak",
    "trough",
    "ratio",
    "interval",
    "interpretation",
    "chart",
    "frequencyButton",
  ];

  connect() {
    this.recalculate();
  }

  recalculate() {
    const result = calculateHalfLife({
      halfLifeHours: Number.parseFloat(this.halfLifeTarget.value),
      frequency: this.frequencyTarget.value,
    });

    this.syncButtons();

    if (result.ok) {
      this.renderResult(result);
    } else {
      this.renderError();
    }
  }

  setPreset(event) {
    this.halfLifeTarget.value = event.currentTarget.dataset.value;
    this.recalculate();
  }

  setFrequency(event) {
    this.frequencyTarget.value = event.currentTarget.dataset.value;
    this.recalculate();
  }

  renderResult(result) {
    this.errorTarget.hidden = true;
    this.resultsTarget.hidden = false;
    this.steadyStateTarget.textContent = formatDuration(result.steadyStateHours);
    this.peakTarget.textContent = `${result.relativePeakAtSteadyState.toFixed(2)}x`;
    this.troughTarget.textContent = `${result.relativeTroughAtSteadyState.toFixed(2)}x`;
    this.ratioTarget.textContent = `${result.peakToTroughRatio.toFixed(2)}x`;
    this.intervalTarget.textContent = `${FREQUENCY_LABELS[this.frequencyTarget.value]} · every ${formatDuration(result.tauHours)}`;
    this.interpretationTarget.textContent = interpretation(result);
    this.renderChart(result.curve);
  }

  renderError() {
    this.resultsTarget.hidden = true;
    this.errorTarget.hidden = false;
  }

  renderChart(curve) {
    const width = 640;
    const height = 280;
    const padding = 28;
    const maxHour = Math.max(...curve.map((sample) => sample.hour));
    const maxLevel = Math.max(...curve.map((sample) => sample.level), 1);
    const points = curve.map((sample) => {
      const x = padding + (sample.hour / maxHour) * (width - padding * 2);
      const y = height - padding - (sample.level / maxLevel) * (height - padding * 2);

      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });

    const doseMarkers = curve
      .filter((sample) => sample.isDose)
      .map((sample) => {
        const x = padding + (sample.hour / maxHour) * (width - padding * 2);

        return `<line x1="${x.toFixed(1)}" y1="${padding}" x2="${x.toFixed(1)}" y2="${height - padding}" stroke="#1a1f2b" stroke-width="1" opacity="0.05"></line>`;
      })
      .join("");

    this.chartTarget.innerHTML = `
      <line x1="${padding}" y1="${height - padding}" x2="${width - padding}" y2="${height - padding}" stroke="#1a1f2b" stroke-width="1" opacity="0.16"></line>
      <line x1="${padding}" y1="${padding}" x2="${padding}" y2="${height - padding}" stroke="#1a1f2b" stroke-width="1" opacity="0.16"></line>
      ${doseMarkers}
      <polyline points="${points.join(" ")}" fill="none" stroke="#4a5470" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"></polyline>
      <circle cx="${points[points.length - 1].split(",")[0]}" cy="${points[points.length - 1].split(",")[1]}" r="7" fill="#4a5470" stroke="#ffffff" stroke-width="2"></circle>
    `;
  }

  syncButtons() {
    this.frequencyButtonTargets.forEach((button) => {
      button.dataset.active = String(button.dataset.value === this.frequencyTarget.value);
    });
  }
}
